import React, { useState, useEffect } from 'react';
import StyledDataTable from '../../components/StyledDataTable/StyledDataTable';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import {
  FaEdit, FaTrash, FaSpinner, FaPlus, FaMoneyBillWave,
  FaCoins, FaUsers, FaSearch, FaHandHoldingUsd
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { handleApiResponse } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuthContext } from '../../context/AuthContext';
import { fundApi } from '../../api/funds';
import { pledgeApi } from '../../api/pledges';
import './Michango.css';
import { useDebounce } from '../../hooks/useDebounce';
import ExportUtility from '../../components/ExportUtility/ExportUtility';
import apiClient from '../../api/index';
import OngezaMchangoModal from '../../modals/michangoModal/OngezaMchangoModal';
import HaririMchangoModal from '../../modals/michangoModal/HaririMchangoModal';
import FutaMchangoModal from '../../modals/michangoModal/FutaMchangoModal';

const Michango = () => {
  const { user, events, currentEvent, isAuthenticated } = useAuthContext();

  // State for data
  const [contributions, setContributions] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI State
  const [perPage, setPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchInput, 500);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (debouncedSearchTerm !== searchTerm) {
      setSearchTerm(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, searchTerm]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContribution, setSelectedContribution] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    if (isAuthenticated && currentEvent) {
      fetchContributions();
      fetchPledges();
    }
  }, [isAuthenticated, currentEvent]);

  const fetchContributions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fundApi.getFunds();
      let funds = response?.data || response || [];

      // Ensure funds is an array
      if (!Array.isArray(funds)) {
        funds = [];
      }

      // Transform data to match component structure
      const formattedContributions = funds.map(fund => ({
        id: fund.id,
        full_name: String(fund.pledger || 'N/A'),
        pledge_id: fund.pledge_id,
        amount: `TZS ${(parseFloat(fund.amount) || 0).toLocaleString()}`,
        amount_raw: parseFloat(fund.amount) || 0,
        description: typeof fund.description === 'string' ? fund.description : '-',
        received_by: String(fund.recorder?.full_name || fund.received_by || 'N/A'),
        date: fund.created_at ? new Date(fund.created_at).toLocaleDateString('sw-TZ') : 'N/A',
        recorded_by: fund.recorded_by,
        status: fund.status
      }));

      setContributions(formattedContributions);
    } catch (err) {
      console.error('Error fetching contributions:', err);
      setContributions([]);
      if (err.response?.status !== 404) {
        setError('Failed to load contributions. Please refresh the page.');
        toast.error('Failed to load contributions');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPledges = async () => {
    try {
      let pledgesList = [];

      try {
        const response = await pledgeApi.getPledges();
        const payload = response?.data;
        if (Array.isArray(payload)) {
          pledgesList = payload;
        } else if (Array.isArray(payload?.pledges)) {
          pledgesList = payload.pledges;
        } else if (Array.isArray(payload?.data)) {
          pledgesList = payload.data;
        } else if (Array.isArray(response)) {
          pledgesList = response;
        }
      } catch (err) {
        console.warn('getPledges API error, trying export endpoint:', err);
      }

      if (!Array.isArray(pledgesList) || pledgesList.length === 0) {
        try {
          const exportRes = await apiClient.get('/export/pledges/json');
          const exportPayload = exportRes?.data;
          const rawPledges = exportPayload?.pledges || exportPayload?.data || exportPayload || [];
          if (Array.isArray(rawPledges) && rawPledges.length > 0) {
            pledgesList = rawPledges;
          }
        } catch (err) {
          console.warn('export/pledges/json error:', err);
        }
      }

      if (!Array.isArray(pledgesList)) {
        pledgesList = [];
      }

      const formattedPledges = pledgesList.map((item, idx) => {
        const pId = item.pledge_id || item.id || (idx + 1);
        const name = item.full_name || item.name || item.guest_name || 'Mgeni';
        const amt = parseFloat(item.pledge_amount || item.pledge || item.amount || 0);
        const collected = parseFloat(item.collected || item.fund || item.paid || item.amount_collected || 0);
        return {
          id: pId,
          pledge_id: pId,
          full_name: name,
          amount: amt,
          amount_collected: collected
        };
      });

      setPledges(formattedPledges);
    } catch (err) {
      console.error('Error fetching pledges:', err);
      setPledges([]);
    }
  };

  const columns = [
    {
      name: '#',
      cell: (row) => filteredData.indexOf(row) + 1,
      width: '60px'
    },
    {
      name: 'Mchangiaji',
      selector: (row) => row.full_name,
      sortable: true,
      grow: 2
    },
    {
      name: 'Kiasi',
      selector: (row) => row.amount,
      sortable: true
    },
    {
      name: 'Maelezo',
      selector: (row) => row.description,
      sortable: true
    },
    {
      name: 'Mpokeaji',
      selector: (row) => row.received_by,
      sortable: true
    },
    {
      name: 'Tarehe',
      selector: (row) => row.date,
      sortable: true
    },
    {
      name: 'Vitendo',
      cell: (row) => (
        <div className="action-buttons">
          <FaEdit
            className="text-muted action-icon opacity-50"
            style={{ cursor: 'not-allowed' }}
            title="Hariri Mchango (Haipatikani kwa sasa)"
          />
          <FaTrash
            className="text-danger action-icon"
            onClick={() => {
              setSelectedContribution(row);
              setShowDeleteModal(true);
            }}
            title="Futa Mchango"
          />
        </div>
      ),
      width: '100px'
    },
  ];

  const filteredData = contributions.filter(contribution =>
    String(contribution.full_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Financial statistics calculations
  const totalAmountCollected = contributions.reduce((sum, item) => sum + (item.amount_raw || 0), 0);
  const totalTransactions = contributions.length;
  const avgContribution = totalTransactions > 0 ? Math.round(totalAmountCollected / totalTransactions) : 0;
  const uniqueContributorsCount = new Set(contributions.map(c => c.full_name)).size;

  // Export Columns definition
  const fundCols = [
    { header: 'Mchangiaji', dataKey: 'name' },
    { header: 'Simu', dataKey: 'phone' },
    { header: 'Kiasi (TZS)', dataKey: 'amount' },
    { header: 'Njia ya Malipo', dataKey: 'channel' },
    { header: 'Tarehe', dataKey: 'date' }
  ];

  // Fetch funds export data from API or state fallback
  const fetchFundsData = async () => {
    try {
      const response = await apiClient.get('/export/funds/json');
      const dataPayload = response?.data;
      const rawData = dataPayload?.funds || dataPayload?.contributions || dataPayload?.data || dataPayload || [];
      if (Array.isArray(rawData) && rawData.length > 0) {
        return rawData.map(item => ({
          name: item.full_name || item.name || item.guest_name || 'N/A',
          phone: item.mobile || item.phone_number || item.phone || 'N/A',
          amount: parseFloat(item.amount || item.fund || item.amount_paid || 0).toLocaleString(),
          channel: item.channel || item.payment_channel || 'Mobile Money',
          date: item.date || item.created_at ? new Date(item.date || item.created_at).toLocaleDateString('sw-TZ') : 'N/A'
        }));
      }
    } catch (err) {
      console.warn('Fallback to local state for funds export:', err);
    }

    if (Array.isArray(contributions) && contributions.length > 0) {
      return contributions.map(item => ({
        name: item.full_name || 'N/A',
        phone: item.phone || 'N/A',
        amount: item.amount_raw ? item.amount_raw.toLocaleString() : (item.amount || '0'),
        channel: 'Mobile Money',
        date: item.date || 'N/A'
      }));
    }
    return [];
  };

  // Custom empty state component
  const EmptyState = () => (
    <div className="empty-state-container">
      <div className="empty-state-icon">
        <FaMoneyBillWave size={64} className="text-muted" />
      </div>
      <h5 className="empty-state-title">Hakuna Michango</h5>
      <p className="empty-state-message">
        Bado hakuna mchango uliorekodiwa. Bonyeza kitufe cha "Ongeza Mchango" kuanza.
      </p>
      <Button
        variant="primary"
        onClick={() => setShowModal(true)}
        className="empty-state-btn"
      >
        <FaPlus className="me-2" />
        Rekodi Mchango wa Kwanza
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="michango-loading">
        <div className="text-center">
          <FaSpinner className="fa-spin" size={48} color="#4169e1" />
          <p className="mt-3">Inapakia michango...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="michango-container py-4 px-2 px-md-4">
      <div className="container-fluid">
        {/* Title & Subtitle Header */}
        <div className="text-center mb-4">
          <h1 className="stylish-heading mb-1" style={{ color: '#4169e1' }}>
            Usimamizi wa Michango
          </h1>
          <p className="text-muted small mb-0">
            Usimamizi na uratibu wa michango na miamala ya shughuli.
          </p>
        </div>

        {/* Executive Stats Summary Row */}
        <div className="row g-2 g-md-3 mb-4 align-items-stretch">
          <div className="col-6 col-md-3">
            <div className="bg-white p-2 p-md-3 rounded-4 shadow-sm border d-flex align-items-center gap-2 h-100">
              <div className="p-2 p-md-3 rounded-3 bg-success-subtle text-success d-flex align-items-center justify-content-center flex-shrink-0">
                <FaMoneyBillWave size={20} />
              </div>
              <div className="overflow-hidden">
                <span className="text-muted d-block fw-medium text-truncate" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Jumla ya Fedha</span>
                <span className="fw-bold fs-6 fs-md-5 text-dark">TZS {totalAmountCollected.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="bg-white p-2 p-md-3 rounded-4 shadow-sm border d-flex align-items-center gap-2 h-100">
              <div className="p-2 p-md-3 rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0">
                <FaCoins size={20} />
              </div>
              <div className="overflow-hidden">
                <span className="text-muted d-block fw-medium text-truncate" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Miamala ya Michango</span>
                <span className="fw-bold fs-6 fs-md-5 text-dark">{totalTransactions}</span>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="bg-white p-2 p-md-3 rounded-4 shadow-sm border d-flex align-items-center gap-2 h-100">
              <div className="p-2 p-md-3 rounded-3 bg-info-subtle text-info d-flex align-items-center justify-content-center flex-shrink-0">
                <FaHandHoldingUsd size={20} />
              </div>
              <div className="overflow-hidden">
                <span className="text-muted d-block fw-medium text-truncate" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Wastani wa Mchango</span>
                <span className="fw-bold fs-6 fs-md-5 text-dark">TZS {avgContribution.toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="col-6 col-md-3">
            <div className="bg-white p-2 p-md-3 rounded-4 shadow-sm border d-flex align-items-center gap-2 h-100">
              <div className="p-2 p-md-3 rounded-3 bg-warning-subtle text-warning d-flex align-items-center justify-content-center flex-shrink-0">
                <FaUsers size={20} />
              </div>
              <div className="overflow-hidden">
                <span className="text-muted d-block fw-medium text-truncate" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>Wachangiaji</span>
                <span className="fw-bold fs-6 fs-md-5 text-dark">{uniqueContributorsCount}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Unified Search & Action Toolbar */}
        <div className="bg-white p-3 rounded-4 shadow-sm border mb-4 d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-3">
          <div className="position-relative flex-grow-1" style={{ maxWidth: '420px' }}>
            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ zIndex: 5 }} />
            <Form.Control
              type="text"
              placeholder="Tafuta kwa jina la mchangiaji..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="ps-5 shadow-none border"
              style={{ borderRadius: '25px', height: '42px', fontSize: '0.85rem', borderColor: '#e2e8f0' }}
            />
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap flex-sm-nowrap">
            <Button
              variant="primary"
              onClick={() => setShowModal(true)}
              className="rounded-pill px-3 px-md-4 py-2 fw-semibold shadow-sm border-0 d-flex align-items-center justify-content-center gap-2 flex-grow-1 flex-sm-grow-0"
              style={{ backgroundColor: '#4169e1', minHeight: '42px' }}
            >
              <FaPlus size={15} />
              Ongeza Mchango
            </Button>

            <ExportUtility
              fetchData={fetchFundsData}
              columns={fundCols}
              filename={`Ripoti_ya_Michango_${currentEvent?.event_title || 'Harusi'}`}
              excelOnlyButton={true}
              excelButtonText="Excel"
              btnClassName="btn btn-outline-success rounded-pill px-3 py-2 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2 flex-grow-1 flex-sm-grow-0"
              btnStyle={{ minHeight: '42px', borderRadius: '25px' }}
            />

            <ExportUtility
              fetchData={fetchFundsData}
              columns={fundCols}
              filename={`Ripoti_ya_Michango_${currentEvent?.event_title || 'Harusi'}`}
              pdfTitle={`Ripoti ya Michango na Mapato - ${currentEvent?.event_title || ''}`}
              pdfOnlyButton={true}
              pdfButtonText="PDF"
              btnClassName="btn btn-outline-danger rounded-pill px-3 py-2 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2 flex-grow-1 flex-sm-grow-0"
              btnStyle={{ minHeight: '42px', borderRadius: '25px' }}
            />
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="mt-3" onClose={() => setError(null)} dismissible>
            <Alert.Heading>Hitilafu!</Alert.Heading>
            <p>{error}</p>
            <Button variant="outline-danger" size="sm" onClick={fetchContributions}>
              Jaribu Tena
            </Button>
          </Alert>
        )}

        {/* Data Table */}
        <div className="michango-table-wrapper">
          {contributions.length > 0 ? (
            <StyledDataTable
              columns={columns}
              data={filteredData}
              paginationRowsPerPageOptions={[50, 100, 200, 500]}
              paginationPerPage={perPage}
              onChangeRowsPerPage={setPerPage}
              emptyMessage="Hakuna matokeo yanayolingana na utafutaji wako."
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>

      {/* Separate External Modal Components */}
      <OngezaMchangoModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSuccess={() => {
          fetchContributions();
          fetchPledges();
        }}
      />

      <HaririMchangoModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedContribution(null);
        }}
        contribution={selectedContribution}
        onSuccess={fetchContributions}
      />

      <FutaMchangoModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setSelectedContribution(null);
        }}
        contribution={selectedContribution}
        onSuccess={() => {
          fetchContributions();
          fetchPledges();
        }}
      />
    </div>
  );
};

export default Michango;